'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { Flame } from 'lucide-react';
import { useNextWordCountdown } from '@/src/hooks/useCountdownToNextDay';
import { StatsContent } from './StatsContent';

export function WinModal({
  onClose,
  isPerfectWin,
  isHardcore = false,
  strings,
  statsStrings,
  storageNamespace,
  refreshKey,
}: {
  onClose: () => void;
  isPerfectWin: boolean;
  isHardcore?: boolean;
  strings: {
    perfectBadge: string;
    perfectTitle: string;
    winTitle: string;
    nextWordInLabel: string;
    closeLabel: string;
  };
  statsStrings: {
    gamesLabelPlural: string;
    gamesLabelSingular: string;
    winRateLabel: string;
    currentStreakLabel: string;
    bestStreakLabel: string;
    performanceTitle: string;
    lossLabel: string;
    timeTitle: string;
    bestTimeLabel: string;
    avgTimeLabel: string;
    worstTimeLabel: string;
  };
  storageNamespace?: string;
  refreshKey?: number;
}) {
  const { hours, minutes, seconds } = useNextWordCountdown();

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/60"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          className={`
            relative max-h-[90vh] w-[92%] max-w-md overflow-y-auto rounded-xl p-5 sm:p-6 text-white
            ${
              isPerfectWin
                ? 'bg-gradient-to-br from-yellow-600 to-amber-700'
                : isHardcore
                ? 'bg-gradient-to-br from-emerald-800 to-emerald-950 ring-1 ring-emerald-500/40'
                : 'bg-neutral-900'
            }
          `}
          initial={{ scale: 0.85, y: 40, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 18 }}
        >
          {/* PERFECT BADGE */}
          {isPerfectWin && (
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="mb-3 flex justify-center"
            >
              <span className="rounded-full bg-yellow-400 px-4 py-1 text-sm font-bold text-black shadow-lg">
                {strings.perfectBadge}
              </span>
            </motion.div>
          )}

          {/* Badge hardcore : la modale de victoire passe au vert (on a gagné),
              ce badge indique que c'était une partie hardcore. */}
          {isHardcore && (
            <div className="mb-2 flex justify-center">
              <span className="flex items-center gap-1.5 rounded-full bg-black/30 px-3 py-1 text-xs font-bold uppercase tracking-widest text-white ring-1 ring-white/20">
                <Flame className="h-3.5 w-3.5 text-orange-400" aria-hidden="true" />
                Hardcore
              </span>
            </div>
          )}

          <h2 className="mb-3 text-xl font-bold text-center">
            {isPerfectWin ? strings.perfectTitle : strings.winTitle}
          </h2>

          {/* Victoire hardcore → stats en vert (pas le rouge hardcore) : le rouge
              est réservé à la défaite. isHardcore={false} force le code couleur vert. */}
          <StatsContent
            isPerfectWin={isPerfectWin}
            isHardcore={false}
            storageNamespace={storageNamespace}
            strings={statsStrings}
            refreshKey={refreshKey}
          />
          {/* Countdown */}
          <div className="mt-4 text-center">
            <p className="text-sm text-neutral-200 mb-1">
              {strings.nextWordInLabel}
            </p>
            <p className="text-lg font-mono">
              {String(hours).padStart(2, '0')}:
              {String(minutes).padStart(2, '0')}:
              {String(seconds).padStart(2, '0')}
            </p>
          </div>

          <button
            onClick={onClose}
            className="mt-4 w-full rounded-md bg-black/40 py-2 hover:bg-black/60 transition font-bold"
          >
            {strings.closeLabel}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
